/**
 * Custom hook for managing device tag
 * Handles device identification and persistence
 */

import { useState, useEffect } from 'react'
import { getOrCreateDeviceTag } from '@/utils/device'

interface UseDeviceTagResult {
  deviceTag: string
  error: Error | null
  retry: () => void
  isLoading: boolean
}

/**
 * Hook to get or create a device tag
 * @returns Object with deviceTag, error state, and retry function
 */
export function useDeviceTag(): UseDeviceTagResult {
  const [deviceTag, setDeviceTag] = useState<string>('')
  const [error, setError] = useState<Error | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const loadDeviceTag = () => {
    setIsLoading(true)
    setError(null)

    getOrCreateDeviceTag()
      .then((tag) => {
        console.log('[useDeviceTag] Device tag retrieved:', tag)
        setDeviceTag(tag)
        setError(null)
      })
      .catch((err) => {
        console.error('[useDeviceTag] Error getting device tag:', err)
        setError(err instanceof Error ? err : new Error(String(err)))
        // Do NOT create fallback tag - let the error surface to the UI
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    loadDeviceTag()
  }, [])

  return {
    deviceTag,
    error,
    retry: loadDeviceTag,
    isLoading,
  }
}


